import type { DictPath } from '../i18n/translate'
import type {
    EmailLogStatus,
    EmailLogType,
    FeedbackCategory,
    FeedbackStatus,
    InvoiceStatus,
    MailTemplateKey,
    TimeEntryType,
} from '../types/api'

export type Tone =
    | 'gray'
    | 'blue'
    | 'green'
    | 'amber'
    | 'red'
    | 'rose'
    | 'sky'
    | 'indigo'
    | 'violet'

export const invoiceStatusMeta: Record<
    InvoiceStatus,
    { labelKey: DictPath; tone: Tone }
> = {
    DRAFT: { labelKey: 'invoiceStatus.draft', tone: 'gray' },
    SENT: { labelKey: 'invoiceStatus.sent', tone: 'blue' },
    PAID: { labelKey: 'invoiceStatus.paid', tone: 'green' },
    CANCELLED: { labelKey: 'invoiceStatus.cancelled', tone: 'red' },
}

export const feedbackCategoryMeta: Record<
    FeedbackCategory,
    { labelKey: DictPath; tone: Tone }
> = {
    BUG: { labelKey: 'feedback.catBug', tone: 'red' },
    IMPROVEMENT: { labelKey: 'feedback.catImprovement', tone: 'blue' },
    FEATURE_REQUEST: { labelKey: 'feedback.catFeatureRequest', tone: 'indigo' },
    OTHER: { labelKey: 'feedback.catOther', tone: 'gray' },
}

export const feedbackStatusMeta: Record<
    FeedbackStatus,
    { labelKey: DictPath; tone: Tone }
> = {
    OPEN: { labelKey: 'feedback.statusOpen', tone: 'amber' },
    READ: { labelKey: 'feedback.statusRead', tone: 'blue' },
    DONE: { labelKey: 'feedback.statusDone', tone: 'green' },
}

export const emailLogStatusMeta: Record<
    EmailLogStatus,
    { labelKey: DictPath; tone: Tone }
> = {
    SENT: { labelKey: 'admin.emailStatusSent', tone: 'green' },
    FAILED: { labelKey: 'admin.emailStatusFailed', tone: 'red' },
}

export const emailLogTypeMeta: Record<
    EmailLogType,
    { labelKey: DictPath; tone: Tone }
> = {
    emailVerification: { labelKey: 'admin.emailTypeVerification', tone: 'blue' },
    emailChange: { labelKey: 'admin.emailTypeChange', tone: 'sky' },
    emailChangeNotice: {
        labelKey: 'admin.emailTypeChangeNotice',
        tone: 'sky',
    },
    passwordReset: { labelKey: 'admin.emailTypePasswordReset', tone: 'indigo' },
    deleteUser: { labelKey: 'admin.emailTypeDeleteUser', tone: 'rose' },
    deleteWorkspace: {
        labelKey: 'admin.emailTypeDeleteWorkspace',
        tone: 'rose',
    },
    workspaceInvite: { labelKey: 'admin.emailTypeInvite', tone: 'violet' },
    workspaceAdded: { labelKey: 'admin.emailTypeAdded', tone: 'violet' },
    invoiceMail: { labelKey: 'admin.emailTypeInvoice', tone: 'blue' },
    dunningMail: { labelKey: 'admin.emailTypeDunning', tone: 'amber' },
    adminTestMail: { labelKey: 'admin.emailTypeTest', tone: 'gray' },
    adminTemplateMail: {
        labelKey: 'admin.emailTypeAdminTemplate',
        tone: 'gray',
    },
    adminFreeMail: { labelKey: 'admin.emailTypeAdminFree', tone: 'gray' },
}

/** Reihenfolge für den Typ-Filter des E-Mail-Log-Panels. */
export const EMAIL_LOG_TYPES: readonly EmailLogType[] = [
    'invoiceMail',
    'dunningMail',
    'workspaceInvite',
    'workspaceAdded',
    'deleteWorkspace',
    'emailVerification',
    'emailChange',
    'emailChangeNotice',
    'passwordReset',
    'deleteUser',
    'adminTestMail',
    'adminTemplateMail',
    'adminFreeMail',
]

/** Vorlagen-Auswahl im Admin-Versand — jede Mail-Art ist in de/en versendbar. */
export const MAIL_TEMPLATE_KEYS = [
    'emailVerification',
    'emailChange',
    'emailChangeNotice',
    'passwordReset',
    'deleteUser',
    'deleteWorkspace',
    'workspaceInvite',
    'workspaceAdded',
    'invoiceMail',
    'dunningMail',
    'adminTestMail',
] as const satisfies readonly MailTemplateKey[]

export const entryTypeMeta: Record<
    TimeEntryType,
    { labelKey: DictPath; tone: Tone }
> = {
    WORK: { labelKey: 'entryType.work', tone: 'indigo' },
    BREAK: { labelKey: 'entryType.break', tone: 'amber' },
    MEETING: { labelKey: 'entryType.meeting', tone: 'violet' },
    ADMIN: { labelKey: 'entryType.admin', tone: 'blue' },
    // Abwesenheiten (Arbeitszeit-Verwaltung) — nie fakturierbar
    SICK: { labelKey: 'entryType.sick', tone: 'rose' },
    VACATION: { labelKey: 'entryType.vacation', tone: 'sky' },
    TIME_OFF: { labelKey: 'entryType.timeOff', tone: 'violet' },
    PAID_ABSENCE: { labelKey: 'entryType.paidAbsence', tone: 'green' },
}

/** Abwesenheits-Typen (Arbeitszeit-Verwaltung) — Dialog statt Timer. */
export const ABSENCE_TYPES: ReadonlySet<TimeEntryType> = new Set([
    'SICK',
    'VACATION',
    'TIME_OFF',
    'PAID_ABSENCE',
])

/** Timer-Kacheln der Zeiterfassung — Abwesenheiten laufen dort nicht. */
export const TRACK_TYPES = [
    'WORK',
    'BREAK',
    'MEETING',
    'ADMIN',
] as const

export type TrackType = (typeof TRACK_TYPES)[number]
